import React, { useEffect, useState, useMemo } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Typography,
  Box,
  Chip,
  IconButton,
  FormControlLabel,
  Grid,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ActionButton from "../../../common/input-fields/ActionButton";
import SendIcon from "@mui/icons-material/Send";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import { ddqQuestions } from "../data/ddqDashboardData";
import {
  getDDQuestionerData,
  getDDQuestionerDataByCompanyId,
} from "../services/apiClient";

const DDQQuestions = (props) => {
  //#region props
  const { companyId, filters, ddqData, loading } = props;

  //#region variables
  const [selected, setSelected] = useState([]);

  //#region memoized filtered questions
  const questions = useMemo(() => {
    if (!ddqData || !Array.isArray(ddqData)) return [];

    return ddqData.filter((question) => {
      // Search filter
      if (
        filters.search &&
        !question.question.toLowerCase().includes(filters.search.toLowerCase())
      ) {
        return false;
      }

      // Category filter
      if (filters.category && question.pillar !== filters.category) {
        return false;
      }

      // Answer status filter
      if (filters.answerStatus) {
        if (filters.answerStatus === "answered") {
          // For "answered" filter, show all answered types
          if (
            !["ai_answered", "manually_answered", "reviewed"].includes(
              question.status
            )
          ) {
            return false;
          }
        } else if (question.status !== filters.answerStatus) {
          return false;
        }
      }

      return true;
    });
  }, [ddqData, filters]);

  const allSelected = selected.length === questions.length;

  //#region click events
  const stopAccordionExpand = (e) => {
    e.stopPropagation();
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(questions.map((question) => question?.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  //#region accordian icons
  const accordianIcons = [
    {
      icon: <CheckCircleIcon fontSize="small" />,
      color: "success",
      key: "check",
    },
    {
      icon: <WarningAmberIcon fontSize="small" />,
      color: "warning",
      key: "warn",
    },
    { icon: <EditIcon fontSize="small" />, color: undefined, key: "edit" },
    { icon: <DeleteIcon fontSize="small" />, color: "error", key: "delete" },
    {
      icon: <ChatBubbleOutlineIcon fontSize="small" />,
      color: undefined,
      key: "chat",
    },
  ];

  //#region useeffect
  useEffect(() => {
    // Reset selected items when questions change
    setSelected([]);
  }, [questions]);

  //#region return
  return (
    <Box className="white-card">
      <div className="space-between">
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={selected.length > 0 && !allSelected}
                onChange={handleSelectAll}
                disabled={loading || questions.length === 0}
                sx={{ "& .MuiSvgIcon-root": { fontSize: 28 } }}
              />
            }
            label="Select All"
            sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: "16px !important", 
                fontWeight: 500,
              },
            }}
          />
        </div>
        <div>
          <ActionButton
            variant="outlined"
            label="Send to company"
            icon={<SendIcon />}
            onClick={() => {}}
            disabled={loading || selected.length === 0}
          />
        </div>
      </div>
      <Grid container spacing={2} mt={2}>
        {loading ? (
          // <Grid item xs={12}>
          //   <Box sx={{ textAlign: 'center', py: 4 }}>
          //     <Typography>Loading questions...</Typography>
          //   </Box>
          // </Grid>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "150px",
              width: "100%",
            }}
          >
            <CircularProgress size={60} color="primary" />
          </Box>
        ) : questions.length === 0 ? (
          <Grid item xs={12}>
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography>
                No questions found matching your criteria.
              </Typography>
            </Box>
          </Grid>
        ) : (
          questions.map((question) => (
            <Accordion
              key={question?.companyQuestionId}
              sx={{ mb: 1, borderRadius: 2, boxShadow: 1, width: "100%" }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon fontSize="medium" />}
                sx={{ alignItems: "center" }}
                onClick={(e) => {
                  if (e.target.closest(".accordion-action-icon")) {
                    e.stopPropagation();
                    return;
                  }
                }}
              >
                <Checkbox
                  checked={selected.includes(question?.companyQuestionId)}
                  onChange={() => handleSelectOne(question?.companyQuestionId)}
                  sx={{ mr: 2 }}
                  onClick={stopAccordionExpand}
                />
                <Box flex={1} onClick={undefined}>
                  <Grid container spacing={2} alignItems="center" mt={0.5}>
                    <Grid item xs={2}>
                      <Chip
                        label={question?.pillar || question?.ddType}
                        size="medium"
                        color="default"
                        sx={{
                          "& .MuiChip-label": {
                            fontWeight: 500,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Chip
                        label={question?.riskCategory}
                        size="small"
                        color={
                          question?.riskCategory === "high"
                            ? "error"
                            : question?.riskCategory === "medium"
                            ? "warning"
                            : "#000"
                        }
                        variant="contained"
                        sx={{
                          "& .MuiChip-label": {
                            fontWeight: 500,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body1">
                        {question?.question}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
                <Box display="flex" gap={1} pr={2}>
                  {accordianIcons.map(({ icon, color, key }) => (
                    <IconButton
                      key={key}
                      size="small"
                      color={color}
                      className="accordion-action-icon"
                      onClick={stopAccordionExpand}
                      sx={{ textTransform: "none !important" }}
                    >
                      {icon}
                    </IconButton>
                  ))}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Box
                    sx={{
                      bgcolor: "#F4F9FC",
                      p: 2,
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    <Typography>AI Answer</Typography>
                    <Typography variant="body2">
                      {question?.aiAnswer || "No AI answer available"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      bgcolor: "#EFFDF4",
                      p: 2,
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    <Typography>Final Answer</Typography>
                    <Typography variant="body2">
                      {question?.finalAnswer || "No final answer available"}
                    </Typography>
                  </Box>
                  {/* {question?.referencedDocuments && question?.referencedDocuments.length > 0 && (
                  <>
                    <Typography mb={1}>Referenced Documents:</Typography>
                    <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                      {question?.referencedDocuments.map((doc, idx) => (
                        <Chip
                          key={doc + idx}
                          icon={<DescriptionIcon />}
                          label={doc}
                          variant="outlined"
                          onClick={() => {}}
                        />
                      ))}
                    </Box>
                  </>
                )} */}
                  {/* {question?.teamDiscussion && question?.teamDiscussion.length > 0 && (
                  <>
                    <Typography mb={1}>Team Discussion</Typography>
                    <Divider sx={{ mb: 2 }} />
                    {question?.teamDiscussion?.map((d, idx) => (
                      <Box display="flex" alignItems="flex-start" mb={2} gap={1} key={idx}>
                        <Box>
                          <Typography>
                            {d.name} {d.date && (
                              <Typography component="span" color="text.secondary">
                                {d.date instanceof Date ? d.date.toLocaleString() : d.date}
                              </Typography>
                            )}
                          </Typography>
                          <Typography variant="body2">
                            {d.description}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </>
                )} */}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Grid>
    </Box>
  );
};

export default DDQQuestions;
